import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';

/** Placeholder block for content that is loading. Purely visual: wrap a group in `LoadingRegion`. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-control bg-neutral-soft', className)}
    />
  );
}

/**
 * Announces "Loading" once to assistive technology while skeletons are shown. With `slowHint`, a line of
 * reassurance appears after `slowAfterMs` so a long wait (reads retry quietly) never looks frozen.
 */
export function LoadingRegion({
  label = 'Loading',
  slowHint = false,
  slowAfterMs = 3000,
  children,
}: {
  label?: string;
  slowHint?: boolean;
  slowAfterMs?: number;
  children: React.ReactNode;
}) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!slowHint) return;
    const timer = window.setTimeout(() => setSlow(true), slowAfterMs);
    return () => window.clearTimeout(timer);
  }, [slowHint, slowAfterMs]);
  return (
    <div role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      {children}
      {slow && (
        <p className="mt-4 text-center text-sm text-muted">
          Still trying to load. Thanks for waiting.
        </p>
      )}
    </div>
  );
}

export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={cn('animate-spin', className)}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
